/**
 * GO4 GoDEX outside-only HTML (Cheetah outside content on 3.94×5.91 in stock).
 * Never includes order items, stub, or prices. Carrier PDF stays a separate path.
 */

import logoSrc from '../images/logo.png';
import { renderOrderQrSvgMap } from './orderQrSvg';
import { formatOrderQrPayload } from './orderQrPayload';
import {
  PACKING_SLIP_DEFAULT_LOGO_URL,
  PACKING_SLIP_STORE_NAME,
} from './packingSlipHtml';
import { CHEETAH_LZ17_CONTENT_SPLIT } from './cheetahLz17Geometry';
import {
  buildCheetahOutsideLabelMarkup,
  resolveCheetahStorageBanner,
} from './cheetahLz17PackingSlipHtml';
import { enrichOrdersWithAccountDeliveryFields } from './enrichOrdersWithAccountDeliveryFields';
import {
  GO4_OUTSIDE_LABEL,
  getGo4OutsideContentBoxMm,
} from './go4OutsideLabelGeometry';

const getOrderNumber = (order) => {
  const raw = String(
    order?.source_id ||
      order?.sourceId ||
      order?.orderNumber ||
      order?.order_number ||
      order?.orderId ||
      order?.id ||
      '',
  ).trim();
  return raw.replace(/^#/, '');
};

/** Re-export split contract keys for GO4 HTML tests. */
export { CHEETAH_LZ17_CONTENT_SPLIT, resolveCheetahStorageBanner };

/**
 * @param {Array|object} orders
 * @param {{
 *   logoUrl?: string,
 *   enrichAccounts?: boolean,
 *   showSignatureLines?: boolean,
 * }} [options]
 */
export async function buildGo4OutsideLabelHTML(orders, options = {}) {
  let list = Array.isArray(orders) ? orders : [orders];
  if (options.enrichAccounts !== false) {
    list = await enrichOrdersWithAccountDeliveryFields(list);
  }

  const logoUrl = options.logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL || logoSrc;
  const showSignatureLines = options.showSignatureLines !== false;
  const stock = GO4_OUTSIDE_LABEL;
  const content = getGo4OutsideContentBoxMm(stock);
  const labelLogoW = stock.logoWidthMm || 26;
  const labelPad = content.padMm;
  const labelTopBreathing = content.topBreathingMm;

  const qrMap = await renderOrderQrSvgMap(list, (o) => getOrderNumber(o), { size: 96 });

  const pages = list
    .map((order) => {
      const orderNumber = getOrderNumber(order);
      if (!orderNumber) return '';
      const qrSvg = qrMap.get(orderNumber) || '';
      const qrPayload = formatOrderQrPayload(orderNumber);
      const markup = buildCheetahOutsideLabelMarkup(order, {
        logoUrl,
        qrSvg,
        qrPayload,
        showSignatureLines,
        contentWidthMm: content.widthMm,
        contentHeightMm: content.heightMm,
        qrFloorMm: stock.qrFloorMm,
        logoAtTop: true,
        brandRowClass: stock.brandRowClass,
      });
      return `
<div class="go4-outside-page" data-print-order="${orderNumber}" data-print-page="go4-outside" data-page-kind="go4-outside">
  ${markup}
</div>`;
    })
    .filter(Boolean)
    .join('\n');

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>GO4 GoDEX outside — ${PACKING_SLIP_STORE_NAME}</title>
<style>
@page {
  size: ${stock.widthMm}mm ${stock.heightMm}mm;
  margin: 0 !important;
}
html, body {
  margin: 0 !important;
  padding: 0 !important;
  width: ${stock.widthMm}mm;
  background: #fff;
  color: #000;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  font-family: Arial, Helvetica, sans-serif;
}
*, *::before, *::after { box-sizing: border-box; }

.go4-outside-page {
  position: relative;
  width: ${stock.widthMm}mm;
  height: ${stock.heightMm}mm;
  overflow: hidden;
  margin: 0;
  padding: 0;
  page-break-after: always;
  break-after: page;
}
.go4-outside-page:last-child {
  page-break-after: auto;
  break-after: auto;
}

/* Outside face fills the GoDEX page. Brand row (logo left, order QR
   right) sits at CSS/physical top of .go4-outside-page (Godex RT730i
   Continuous Roll leading edge). Any contentRotationDeg applies only
   to .lz-label__body below the brand row. */
.go4-outside-page .lz-label {
  width: 100%;
  height: 100%;
  padding: ${labelTopBreathing + labelPad}mm ${labelPad}mm ${labelPad}mm ${labelPad}mm !important;
  font-size: var(--lz-base, 9pt);
  line-height: 1.25;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: var(--lz-gap, 1.5mm);
  overflow: hidden;
  box-sizing: border-box;
  transform: none;
}
.go4-outside-page .lz-label > * {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}
.go4-outside-page .lz-label__body {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: var(--lz-gap, 1.5mm);
  overflow: hidden;
  transform: rotate(${content.contentRotationDeg}deg);
  transform-origin: center center;
}
.go4-outside-page .lz-label__body > * {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}
.go4-outside-page .lz-ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.go4-outside-page .lz-ul li {
  margin: 0;
  padding: 0;
  line-height: var(--lz-meta-line, 5mm);
}
.go4-outside-page .lz-ship {
  font-size: var(--lz-ship, 11pt);
  line-height: calc(var(--lz-meta-line, 5mm) * 1.05);
  font-weight: 700;
}
.go4-outside-page .lz-label__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2.5mm;
}
.go4-outside-page .lz-label--logo-at-top .lz-label__top {
  display: block;
  width: 100%;
}
.go4-outside-page .lz-label__meta { flex: 1 1 auto; min-width: 0; }
.go4-outside-page .lz-label__company {
  margin: 0;
  padding: 0.8mm 0 0;
  border-top: 0.3mm solid #111;
  font-weight: 800;
  letter-spacing: 0.015em;
  text-transform: uppercase;
  color: #111;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.go4-outside-page .go4-outside-brand-row,
.go4-outside-page .lz-label--logo-at-top > .lz-label__mark,
.go4-outside-page .lz-label__mark[data-go4-brand="top"] {
  flex: 0 0 auto;
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2.5mm;
  text-align: left;
  order: 0;
}
.go4-outside-page .lz-brand-logo {
  width: ${labelLogoW}mm !important;
  max-width: ${labelLogoW}mm !important;
  height: auto !important;
  max-height: none !important;
  object-fit: contain !important;
  object-position: left top !important;
  display: block !important;
  margin: 0 !important;
  flex: 0 0 auto;
  border: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.go4-outside-page .lz-qr {
  margin: 0;
  margin-left: auto;
  width: auto;
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}
.go4-outside-page .lz-qr svg {
  width: var(--lz-qr-mm, 16mm);
  height: var(--lz-qr-mm, 16mm);
  display: block;
}
.go4-outside-page .lz-addr {
  border-top: 0.3mm solid #333;
  padding-top: 0.8mm !important;
  overflow: hidden;
  line-height: var(--lz-addr-line, 4.5mm);
}
.go4-outside-page .lz-notes {
  border-top: 0.2mm dashed #666;
  padding-top: 0.6mm !important;
  font-size: var(--lz-notes, 8pt);
  line-height: var(--lz-notes-line, 3.5mm);
  overflow: hidden;
}
.go4-outside-page .lz-cold {
  background: #111;
  color: #fff;
  font-weight: 700;
  font-size: 6.5pt;
  min-height: calc(var(--lz-cold-mm, 8mm) - 1.5mm);
  padding: 0.8mm 1.2mm !important;
  text-align: center;
  line-height: 1.2;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.go4-outside-page .lz-sigs {
  display: flex;
  justify-content: space-between;
  gap: 2.5mm;
  font-size: 7pt;
  border-top: 0.2mm solid #999;
  padding-top: 0.8mm !important;
  line-height: 1.2;
}

/* Hard forbid stub / product / price chrome on GoDEX face */
.go4-outside-page .product_table,
.go4-outside-page .lz-stub-table,
.go4-outside-page [data-cheetah-face="stub"],
.go4-outside-page .receipt_footer {
  display: none !important;
}
</style>
</head>
<body>
${pages}
</body>
</html>`;
}

export default buildGo4OutsideLabelHTML;
